import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import styled, { createGlobalStyle, css, ThemeProvider } from 'styled-components';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Configuration du thème "Law Firm Premium"
const theme = {
  colors: {
    primary: '#C1A68D',       // Doré/Sable
    secondary: '#A88D73',     // Sable plus sombre (hover)
    success: '#8CA38D',       // Vert sauge discret
    error: '#D97A7A',         // Rouge sourd
    background: 'transparent',
    surface: 'rgba(26, 26, 26, 0.6)', // Gris anthracite translucide
    text: '#F5F0E6',          // Blanc cassé
    textSecondary: '#D4C5B0', // Beige grisé
    border: 'rgba(193, 166, 141, 0.2)', // Bordure beige discrète
    focus: 'rgba(193, 166, 141, 0.1)'
  },
  breakpoints: {
    mobile: '768px',
    tablet: '1024px'
  },
  animations: {
    spring: { type: "spring", stiffness: 200, damping: 25 }, // Animation plus lourde/premium
    smooth: { duration: 0.4, ease: "easeOut" }
  }
};

// Styles globaux optimisés
const GlobalStyle = createGlobalStyle`
  input, select, textarea, button {
    font-family: inherit;
    font-size: 16px; /* Empêche le zoom iOS */
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    input, select, textarea {
      font-size: 16px !important;
    }
  }
`;

const useMobileDetection = () => {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      const userAgent = navigator.userAgent.toLowerCase();
      const isMobileDevice = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i.test(userAgent);
      const isSmallScreen = window.innerWidth <= 768;
      setIsMobile(isMobileDevice || isSmallScreen);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return isMobile;
};

// Composants styled
const Container = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  padding: ${props => props.isMobile ? '16px' : '24px'};
  position: relative;
  z-index: 2;
`;

const Header = styled(motion.header)`
  text-align: center;
  margin-bottom: 32px;
  position: relative;
`;

const HeaderTitle = styled.h2`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  font-size: ${props => props.isMobile ? '1.8rem' : '2.2rem'};
  font-weight: 700;
  color: ${theme.colors.text};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 16px;
  
  i {
    color: ${theme.colors.primary};
  }
`;

const FormWrapper = styled(motion.div)`
  position: relative;
  background: ${theme.colors.surface};
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  border-radius: 4px;
  padding: ${props => props.isMobile ? '24px 20px' : '40px'};
  border: 1px solid ${theme.colors.border};
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
  margin-bottom: 24px;
  overflow: hidden;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: ${props => props.isMobile ? '1fr' : 'repeat(2, 1fr)'};
  gap: ${props => props.isMobile ? '20px' : '24px'};
  margin-bottom: 32px;
`;

const FormGroup = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  position: relative;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 500;
  color: ${theme.colors.textSecondary};
  
  i {
    color: ${theme.colors.primary};
  }
`;

const InputBase = css`
  padding: ${props => props.isMobile ? '14px 16px' : '16px 20px'};
  background: rgba(10, 10, 10, 0.6);
  border: 1px solid ${props => props.hasError ? theme.colors.error : theme.colors.border};
  border-radius: 2px;
  font-size: 15px;
  color: ${theme.colors.text};
  transition: all 0.3s ease;
  outline: none;
  width: 100%;
  
  &:focus {
    border-color: ${theme.colors.primary};
    background: rgba(10, 10, 10, 0.9);
  }
  
  &::placeholder {
    color: rgba(212, 197, 176, 0.4);
    font-weight: 300;
  }
  
  appearance: none;
  -webkit-appearance: none;
`;

const Input = styled.input`
  ${InputBase}
`;

// L'icône de la flèche (Select) a été convertie en beige (#C1A68D)
const Select = styled.select`
  ${InputBase}
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='%23C1A68D'%3e%3cpath d='M7 10l5 5 5-5z'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 16px center;
  background-size: 20px;
  padding-right: 48px;
`;

const Textarea = styled.textarea`
  ${InputBase}
  min-height: ${props => props.isMobile ? '120px' : '150px'};
  resize: vertical;
  font-family: inherit;
`;

const SubmitButton = styled(motion.button)`
  width: 100%;
  padding: ${props => props.isMobile ? '16px' : '20px'};
  background: ${props => props.isSubmitted ? theme.colors.success : theme.colors.primary};
  border: 1px solid ${props => props.isSubmitted ? theme.colors.success : theme.colors.primary};
  border-radius: 2px;
  font-size: ${props => props.isMobile ? '15px' : '16px'};
  text-transform: uppercase;
  letter-spacing: 1px;
  font-weight: 600;
  color: #1A1A1A;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  transition: all 0.3s ease;
  opacity: ${props => props.disabled && !props.isSubmitted ? 0.7 : 1};
  
  ${props => !props.disabled && css`
    &:hover {
      background: ${theme.colors.secondary};
      border-color: ${theme.colors.secondary};
    }
  `}
  
  &:active {
    transform: ${props => !props.disabled ? 'scale(0.99)' : 'none'};
  }
`;

const FooterInfo = styled.footer`
  text-align: center;
  font-size: 13px;
  color: ${theme.colors.textSecondary};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  font-weight: 300;
  
  i {
    color: ${theme.colors.primary};
  }
`;

const LoadingOverlay = styled(motion.div)`
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(10, 10, 10, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
  backdrop-filter: blur(4px);
`;

const ContactForm = () => {
  const isMobile = useMobileDetection();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const validateField = useCallback((name, value) => {
    let error = '';
    switch (name) {
      case 'fullName':
        if (value.length < 2) error = 'Veuillez saisir votre nom complet.';
        break;
      case 'email':
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) error = 'Format d\'email invalide.';
        break;
      case 'subject':
        if (!value) error = 'Veuillez sélectionner un motif.';
        break;
      case 'message':
        if (value.length < 10) error = 'Votre message est trop court.';
        break;
      default:
        break;
    }
    return error;
  }, []);

  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    const error = validateField(name, value);
    setErrors(prev => ({ ...prev, [name]: error }));
  }, [validateField]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    
    const newErrors = {};
    Object.keys(formData).forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error('Veuillez corriger les erreurs dans le formulaire.', {
        style: { background: '#1A1A1A', color: '#F5F0E6', border: '1px solid #D97A7A' }
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch('https://kmj-lawfirm.com/contacts.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const result = await response.json();
      
      if (result.status === 'success') {
        setIsSubmitted(true);
        toast.success('Votre message a été transmis avec succès.', {
          style: { background: '#1A1A1A', color: '#F5F0E6', border: '1px solid #C1A68D' },
          icon: '⚖️'
        });
        
        setTimeout(() => {
          setIsSubmitted(false);
          setFormData({ fullName: '', email: '', phone: '', subject: '', message: '' });
        }, 4000);
      } else {
        toast.error(result.message || 'Une erreur est survenue lors de l\'envoi.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Erreur réseau. Veuillez réessayer plus tard.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, validateField]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: theme.animations.smooth }
  };

  const buttonContent = useMemo(() => {
    if (isSubmitted) {
      return (
        <>
          <i className="fas fa-check" />
          Message Transmis
        </>
      );
    }
    if (isSubmitting) {
      return (
        <>
          <i className="fas fa-circle-notch fa-spin" />
          Transmission...
        </>
      );
    }
    return (
      <>
        <i className="fas fa-envelope" />
        Envoyer le message
      </>
    );
  }, [isSubmitted, isSubmitting]);

  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <Container isMobile={isMobile} variants={containerVariants} initial="hidden" animate="visible">
        
        <Header variants={itemVariants}>
          <HeaderTitle isMobile={isMobile}>
            <i className="fas fa-pen-fancy" style={{ fontSize: isMobile ? '20px' : '24px' }} />
            Formulaire de Contact
          </HeaderTitle>
          <motion.p 
            style={{ 
              fontSize: isMobile ? '14px' : '15px', 
              color: theme.colors.textSecondary,
              maxWidth: '600px',
              margin: '0 auto',
              lineHeight: 1.6,
              fontWeight: 300
            }}
          >
            Veuillez détailler votre requête ci-dessous. Un de nos avocats ou conseillers juridiques vous répondra dans les plus brefs délais.
          </motion.p>
        </Header>
        
        <FormWrapper isMobile={isMobile} variants={itemVariants}>
          {/* Lueur de fond discrète */}
          <div style={{
            position: 'absolute', width: '300px', height: '300px',
            background: 'radial-gradient(circle, rgba(193, 166, 141, 0.05) 0%, transparent 70%)',
            top: '-150px', right: '-150px', zIndex: 0, pointerEvents: 'none'
          }} />

          {/* Overlay de chargement localisé au formulaire */}
          <AnimatePresence>
            {isSubmitting && (
              <LoadingOverlay initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                <i className="fas fa-circle-notch fa-spin" style={{ fontSize: '30px', color: theme.colors.primary }} />
              </LoadingOverlay>
            )}
          </AnimatePresence>
          
          <form onSubmit={handleSubmit} style={{ position: 'relative', zIndex: 2 }}>
            <FormGrid isMobile={isMobile}>
              {[
                { id: 'fullName', label: 'Nom complet', icon: 'fa-user', type: 'text', placeholder: 'Ex: Jean Dupont' },
                { id: 'email', label: 'Email Professionnel', icon: 'fa-envelope', type: 'email', placeholder: 'contact@entreprise.com' },
                { id: 'phone', label: 'Téléphone (Optionnel)', icon: 'fa-phone', type: 'tel', placeholder: '+243 ...' },
                { id: 'subject', label: 'Motif de consultation', icon: 'fa-briefcase', type: 'select' }
              ].map((field) => (
                <FormGroup key={field.id}>
                  <Label isMobile={isMobile}>
                    <i className={`fas ${field.icon}`} />
                    {field.label}
                  </Label>
                  {field.type === 'select' ? (
                    <Select
                      id={field.id}
                      name={field.id}
                      value={formData[field.id]}
                      onChange={handleChange}
                      required
                      isMobile={isMobile}
                      hasError={!!errors[field.id]}
                    >
                      <option value="" disabled>Sélectionnez un domaine</option>
                      <option value="Droit des Sociétés">Droit des Sociétés</option>
                      <option value="Expertise Corporative">Expertise Corporative</option>
                      <option value="Droit Financier">Droit Financier</option>
                      <option value="Contentieux Civil">Contentieux Civil / Commercial</option>
                      <option value="Droit des Assurances">Droit des Assurances</option>
                      <option value="Droit Minier">Droit Minier</option>
                      <option value="Environnement et Hydrocarbures">Environnement & Hydrocarbures</option>
                      <option value="RSE">Responsabilité Sociale (RSE)</option>
                      <option value="Autre">Autre demande</option>
                    </Select>
                  ) : (
                    <Input
                      type={field.type}
                      id={field.id}
                      name={field.id}
                      value={formData[field.id]}
                      onChange={handleChange}
                      placeholder={field.placeholder}
                      required={field.id !== 'phone'}
                      isMobile={isMobile}
                      hasError={!!errors[field.id]}
                    />
                  )}
                  <AnimatePresence>
                    {errors[field.id] && (
                      <motion.div
                        initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                        style={{ color: theme.colors.error, fontSize: '12px', marginTop: '4px' }}
                      >
                        {errors[field.id]}
                      </motion.div>
                    )}
                  </AnimatePresence>
                </FormGroup>
              ))}
              
              <FormGroup style={{ gridColumn: '1 / -1' }}>
                <Label isMobile={isMobile}>
                  <i className="fas fa-align-left" />
                  Détail de votre requête
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Veuillez exposer les faits de manière précise..."
                  required
                  isMobile={isMobile}
                  hasError={!!errors.message}
                />
                <AnimatePresence>
                  {errors.message && (
                    <motion.div
                      initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                      style={{ color: theme.colors.error, fontSize: '12px', marginTop: '4px' }}
                    >
                      {errors.message}
                    </motion.div>
                  )}
                </AnimatePresence>
              </FormGroup>
            </FormGrid>
            
            <SubmitButton
              type="submit"
              disabled={isSubmitting || isSubmitted}
              isSubmitted={isSubmitted}
              isMobile={isMobile}
              whileTap={{ scale: isSubmitting || isSubmitted ? 1 : 0.98 }}
            >
              {buttonContent}
            </SubmitButton>
          </form>
        </FormWrapper>
        
        <FooterInfo variants={itemVariants}>
          <i className="fas fa-lock" />
          <span>Toutes les informations transmises sont soumises au secret professionnel.</span>
        </FooterInfo>
      </Container>

      {/* Notifications stylisées */}
      <ToastContainer
        position={isMobile ? "top-center" : "bottom-right"}
        autoClose={4000}
        hideProgressBar
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        toastStyle={{ backgroundColor: '#1A1A1A', color: '#F5F0E6', borderRadius: '4px' }}
      />
    </ThemeProvider>
  );
};

export default ContactForm;